import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { caseStudies } from '../data/content';
import SafeIcon from '../common/SafeIcon';

const CaseStudyDetail = () => {
  const { id } = useParams();
  const study = caseStudies.find(cs => cs.id === id);

  if (!study) return <div className="pt-32 text-center text-xl font-heading font-bold">Engagement not found.</div>;

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }}
      className="pt-24 min-h-screen bg-white"
    >
      <div className="bg-brand-dark py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.1] pointer-events-none" style={{ backgroundImage: 'linear-gradient(to right, #40E0D0 1px, transparent 1px), linear-gradient(to bottom, #40E0D0 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <Link to="/work" className="inline-flex items-center gap-2 text-xs uppercase font-bold tracking-widest text-brand-cyan hover:text-white mb-12 transition-colors">
            <SafeIcon name="ArrowLeft" className="w-4 h-4" /> Back to Engagements
          </Link>
          
          <span className="text-xs uppercase tracking-[0.2em] text-brand-yellow font-bold mb-4 block">{study.category}</span>
          <h1 className="text-4xl md:text-6xl font-heading font-bold text-white mb-8 leading-tight max-w-4xl">{study.title}</h1>
          <p className="text-xl text-slate-300 font-medium leading-relaxed mb-4">
            Client: <span className="text-white font-bold">{study.client}</span>
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 items-start">
          <div className="lg:col-span-2 space-y-16">
            <section>
              <h3 className="text-xl font-heading font-bold text-brand-dark mb-6 flex items-center gap-3">
                <SafeIcon name="Target" className="w-6 h-6 text-brand-red" /> The Challenge
              </h3>
              {typeof study.challenge === 'object' ? (
                <>
                  <p className="text-slate-600 leading-relaxed font-medium text-lg mb-4">{study.challenge.text}</p>
                  <ul className="list-disc list-outside pl-6 text-slate-600 space-y-2">
                    {study.challenge.bullets.map((bullet, idx) => (
                      <li key={idx} className="font-medium text-lg">{bullet}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="text-slate-600 leading-relaxed font-medium text-lg">{study.challenge}</p>
              )}
            </section>

            <section>
              <h3 className="text-xl font-heading font-bold text-brand-dark mb-6 flex items-center gap-3">
                <SafeIcon name="Layers" className="w-6 h-6 text-brand-cyan" /> The Orchestration
              </h3>
              {typeof study.solution === 'object' ? (
                <>
                  <p className="text-slate-600 leading-relaxed font-medium text-lg mb-4">{study.solution.text}</p>
                  <ul className="list-disc list-outside pl-6 text-slate-600 space-y-2">
                    {study.solution.bullets.map((bullet, idx) => (
                      <li key={idx} className="font-medium text-lg">{bullet}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="text-slate-600 leading-relaxed font-medium text-lg">{study.solution}</p>
              )}
            </section>

            <section>
              <h3 className="text-xl font-heading font-bold text-brand-dark mb-6 flex items-center gap-3">
                <SafeIcon name="TrendingUp" className="w-6 h-6 text-brand-yellow" /> Strategic Impact
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {study.results.map((res, i) => (
                  <div key={i} className="flex items-center gap-4 p-6 bg-slate-50 border border-slate-200 rounded-xl">
                    <SafeIcon name="CheckCircle" className="w-6 h-6 text-brand-cyan shrink-0" />
                    <span className="text-sm font-bold text-brand-dark">{res}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="lg:col-span-1 sticky top-32 space-y-8">
            <div className="aspect-[4/5] overflow-hidden shadow-2xl border-4 border-white rounded-xl bg-slate-100">
              <img src={study.image} alt={study.title} className="w-full h-full object-cover" />
            </div>
            
            <div className="p-8 bg-brand-dark rounded-xl text-white shadow-xl border border-brand-cyan/30">
              <h4 className="text-xs uppercase tracking-widest text-brand-cyan mb-6 font-bold">Orchestration Stack</h4>
              <div className="flex flex-wrap gap-2">
                {study.tech.map((t, i) => (
                  <span key={i} className="px-3 py-1 bg-white/10 text-[10px] uppercase font-bold tracking-widest rounded text-white">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CaseStudyDetail;